import type {
  ProcedureTemplateListItem,
  ProcedureTemplateDetail,
  ProcedureJourney,
  FormCategory,
  ProcedureType,
  SequenceStepInput,
  VersionAuditEntry,
} from '@/types/cgt'
import type {
  ProcedureTemplateExport,
  ProcedureTemplateImportChoices,
  ProcedureTemplateImportPlan,
} from '@/types/procedureTemplateTransfer'

import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders(init?.headers),
    ...init,
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    // Nest wraps validation errors in { message: string | string[] }.
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) {
        message = Array.isArray(parsed.message)
          ? parsed.message.join(', ')
          : parsed.message
      }
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

export const procedureTemplatesApi = {
  /**
   * Flat list of templates.
   *
   * `offerable: true` is what any surface that lets a user PICK a template
   * wants: one row per family, its current ACTIVE version. Without it the list
   * carries every version of every family — so a family that has been through
   * "Update to Latest Form" appears twice under the same name, and retired
   * versions are offered alongside live ones (z8nrz7ckjg).
   *
   * The unfiltered form is still right for surfaces that show version HISTORY.
   */
  list: (opts: { offerable?: boolean; search?: string } = {}): Promise<ProcedureTemplateListItem[]> => {
    const query = new URLSearchParams({ isTestData: 'false' })
    if (opts.offerable) {
      query.set('active', 'true')
      query.set('latestOnly', 'true')
    }
    if (opts.search) query.set('search', opts.search)
    return request<ProcedureTemplateListItem[]>(`/procedure-templates?${query}`)
  },

  /**
   * Paginated variant for the Procedure Templates table — `{ items, total }`.
   *
   * `type` filters SERVER-side on purpose: the table is server-paged, so
   * narrowing the returned page would hide matches on other pages and leave the
   * `n–m of N templates` footer counting the unfiltered total.
   */
  listPaged: (params: {
    skip: number
    take: number
    type?: ProcedureType
    /** Only templates with at least one form of this Form Type. Server-side, like `type`. */
    formCategory?: FormCategory
    /**
     * Case-insensitive substring match on name, product code, and the
     * TPL-xxxxx unique id. Server-side for the same reason `type` is: the
     * table is server-paged, so filtering here would search one page and
     * leave the footer counting the unfiltered total.
     */
    search?: string
    /** Status filter. Omit for "any"; the backend reads it as a boolean. */
    active?: boolean
    groupByFamily?: boolean
    /**
     * Column sort, `<column>:<asc|desc>` (z8nrz7e4h6). Orders FAMILIES before
     * paging, so the backend accepts it only together with `groupByFamily`.
     */
    sort?: string
  }): Promise<{ items: ProcedureTemplateListItem[]; total: number }> => {
    const query = new URLSearchParams({
      isTestData: 'false',
      skip: String(params.skip),
      take: String(params.take),
      ...(params.type ? { type: params.type } : {}),
      ...(params.formCategory ? { formCategory: params.formCategory } : {}),
      // Falsy-guarded rather than `!== undefined`: an empty search must not be
      // sent as `search=`, which reads server-side as a real empty term.
      ...(params.search ? { search: params.search } : {}),
      ...(params.active !== undefined ? { active: String(params.active) } : {}),
      ...(params.groupByFamily ? { groupByFamily: 'true' } : {}),
      ...(params.sort ? { sort: params.sort } : {}),
    })
    return request<{ items: ProcedureTemplateListItem[]; total: number }>(
      `/procedure-templates?${query}`,
    )
  },

  get: (id: string): Promise<ProcedureTemplateDetail> =>
    request<ProcedureTemplateDetail>(`/procedure-templates/${id}`),

  // "Update Procedure Template to Latest Form": mints a NEW signed template
  // version with the same flow, pointing at a newer ACTIVE form version. The
  // old template version stays intact (86eybvqgn).
  createVersion: (
    id: string,
    payload: {
      oldFormDefinitionId: string
      newFormDefinitionId: string
      version?: number
      // Single-use proof the signer re-entered their password; spent server-side.
      // No `signedBy`: the server resolves the signer from the principal.
      signingToken: string
      reason?: string
    },
  ): Promise<ProcedureTemplateDetail> =>
    request<ProcedureTemplateDetail>(`/procedure-templates/${id}/versions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Signed ACTIVE/INACTIVE toggle. Inactivation cascades to form versions
  // linked only to this template — the response lists them.
  setStatus: (
    id: string,
    payload: { active: boolean; signingToken: string; reason?: string },
  ): Promise<{ procedureTemplate: ProcedureTemplateListItem; cascadedForms: Array<{ id: string; name: string; version: number }> }> =>
    request<{ procedureTemplate: ProcedureTemplateListItem; cascadedForms: Array<{ id: string; name: string; version: number }> }>(
      `/procedure-templates/${id}/status`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    ),

  /** Version-control events for the template's whole family, newest first. */
  versionAudit: (id: string): Promise<VersionAuditEntry[]> =>
    request<VersionAuditEntry[]>(`/procedure-templates/${id}/version-audit`),

  create: (payload: {
    name: string
    description?: string
    createdBy?: string
    type?: string
  }): Promise<ProcedureTemplateDetail> =>
    request<ProcedureTemplateDetail>('/procedure-templates', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: (
    id: string,
    payload: {
      name?: string
      description?: string
      productCode?: string | null
      type?: string
      autoSelect?: boolean
      /** Days after the scheduled start a run is due. Null clears the default. */
      dueOffsetDays?: number | null
    },
  ): Promise<ProcedureTemplateDetail> =>
    request<ProcedureTemplateDetail>(`/procedure-templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  /**
   * "Copy Procedure Template" (z8nrz7cz21): duplicates this VERSION and every
   * form in its sequence into a brand-new family.
   *
   * Not to be confused with `createVersion`, which mints the next version of
   * the SAME family still pointing at the same forms. This shares nothing with
   * the source — the copy's forms are new families of their own, so editing one
   * cannot touch the original.
   *
   * Unsigned, unlike createVersion/setStatus: a copy creates a new artifact and
   * alters nothing already in use. `productCode` and `autoSelect` are
   * deliberately not carried over by the server.
   *
   * A form of the sequence omitted from `formNames` is copied as
   * "Copy of <name>".
   */
  copy: (
    id: string,
    payload: { name: string; formNames?: Array<{ formDefinitionId: string; name: string }> },
  ): Promise<ProcedureTemplateDetail> =>
    request<ProcedureTemplateDetail>(`/procedure-templates/${id}/copy`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /**
   * Export this VERSION as a portable bundle (z8nrz7dz4g).
   *
   * Distinct from `copy`, which duplicates a template INSIDE one database and
   * so cannot leave the environment it runs in. A bundle addresses everything
   * by handle and human key, so it can be carried dev → staging → prod.
   *
   * `warnings` is non-empty when a requirement line was left out because its
   * material or equipment no longer exists — the export still succeeds, because
   * a dangling reference is the author's to fix, not a reason to block.
   */
  exportBundle: (id: string): Promise<ProcedureTemplateExport> =>
    request<ProcedureTemplateExport>(`/procedure-templates/${id}/export`),

  /**
   * Dry-run an import. Writes nothing.
   *
   * Returns the very plan a commit would execute, so what the preview shows and
   * what the import does cannot drift. `plan.canCommit` is false while
   * `plan.blockers` is non-empty.
   */
  previewImport: (
    bundle: unknown,
    choices?: ProcedureTemplateImportChoices,
  ): Promise<{ plan: ProcedureTemplateImportPlan }> =>
    request<{ plan: ProcedureTemplateImportPlan }>('/procedure-templates/import/preview', {
      method: 'POST',
      body: JSON.stringify({ bundle, choices }),
    }),

  /**
   * Commit an import as a NEW family at V1. Unsigned, like `copy`.
   *
   * Re-plans server-side against a fresh read rather than trusting the preview,
   * so a material deleted between the two is caught rather than half-applied.
   */
  commitImport: (
    bundle: unknown,
    choices?: ProcedureTemplateImportChoices,
  ): Promise<ProcedureTemplateDetail> =>
    request<ProcedureTemplateDetail>('/procedure-templates/import', {
      method: 'POST',
      body: JSON.stringify({ bundle, choices }),
    }),

  /**
   * Commit an import as the NEXT VERSION of the family the bundle names.
   *
   * SIGNED, matching `createVersion`: this changes a family that may already be
   * in use, rather than adding an artifact beside it.
   *
   * `signingToken` goes in the BODY, not a header — SignatureGuard reads
   * `req.body.signingToken`, and the global ValidationPipe is
   * `forbidNonWhitelisted`, so a header would be both unread and a 400.
   */
  commitImportAsVersion: (
    bundle: unknown,
    choices: ProcedureTemplateImportChoices,
    signingToken: string,
  ): Promise<ProcedureTemplateDetail> =>
    request<ProcedureTemplateDetail>('/procedure-templates/import/version', {
      method: 'POST',
      body: JSON.stringify({
        bundle,
        choices: { ...choices, asNewVersion: true },
        signingToken,
      }),
    }),

  replaceSequence: (
    id: string,
    steps: SequenceStepInput[],
  ): Promise<ProcedureTemplateDetail> =>
    request<ProcedureTemplateDetail>(`/procedure-templates/${id}/sequence`, {
      method: 'PUT',
      body: JSON.stringify({ steps }),
    }),

  /**
   * `patientMrn` is nullable because an MES order has no patient (z8nrz7ckj9).
   * The server takes `procedureId` alone as a complete scope and documents the
   * MRN as "omit only for a procedure with no patient" — so pass one or both,
   * never neither.
   *
   * Omitted rather than sent empty: `URLSearchParams({ patientMrn })` stringifies
   * null into the literal "null", which reads server-side as a real MRN and
   * matches no patient, producing an empty journey instead of an error.
   */
  journey: (
    id: string,
    patientMrn: string | null,
    procedureId?: string,
  ): Promise<ProcedureJourney> => {
    const params = new URLSearchParams()
    if (patientMrn) params.append('patientMrn', patientMrn)
    if (procedureId) params.append('procedureId', procedureId)
    return request<ProcedureJourney>(`/procedure-templates/${id}/journey?${params}`)
  },

  delete: (id: string): Promise<{ ok: true }> =>
    request<{ ok: true }>(`/procedure-templates/${id}`, { method: 'DELETE' }),
}
